import { useEffect, useState } from "react";
import {
  ActionIcon,
  ActionTable,
  DurationSummary,
  InteractionSummary,
  StyledContainer,
  StyledHeader
} from "../Encounters/Encounters.styles";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Icon,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from "@mui/material";
import SimpleGrid, { BodyProps } from "components/shared/SimpleGrid/SimpleGrid";
import { useTranslation } from "react-i18next";
import {
  EncounterInterface,
  EntryInterface
} from "context/initialState/Encounter";
import API from "api";
import Session from "helpers/session";
import EncounterTimer from "components/shared/EncounterTimer/EncounterTimer";
import { useParams } from "react-router-dom";
import moment from "moment";

export default function Encounters() {
  const { memberId } = useParams();
  const { t } = useTranslation();
  // const TableHeaders: CellProps[] = [ {content: t('u-starts-at'), align: 'left'}, {content: t('u-all'), align: 'left'}, {content: 'Note', align: 'left'}, {content: t('u-actor'), align: 'left'} ];
  const [totalEncounterList, setTotalEncounterList] = useState<number>(0);
  const [membersEncounters, setMembersEncounters] = useState<
    EncounterInterface[] | undefined
  >([]);

  const getAllCoordinatorEncounters = async (
    memberId: string
  ): Promise<EncounterInterface[]> => {
    return API.kore.encounters.getByMember(
      Session.crackCookieToken("coordinator"),
      memberId
    );
  };

  const paginationAction = (itemsPerPage?: number, currentPage?: number) => {
    getAllCoordinatorEncounters(memberId || "").then(
      (_encounters: EncounterInterface[]) => {
        setMembersEncounters(_encounters);
      }
    );
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case "message":
        return "mail_outline";
      case "note":
        return "sticky_note_2";
      // case "readings review":
      //     return "add_chart_outline";
      case "profile change":
        return "manage_accounts_outline";
      case "intervention":
        return "interpreter_mode_outline";
      case "readings review":
        return "grading_outline";
      default:
        return "question_outline";
    }
  };

  const formatDuration = (duration: string | number): string => {
    if (Number(duration) === 0) return "N/A";
    if (Number(duration) > 60 * 60 * 24 * 2)
      return `2+ days (${duration} secs)`;
    // greater than 1 min
    if (Number(duration) > 60)
      return (
        moment.duration(Number(duration), "seconds").get("minutes") + " mins"
      );
    // less than 1 min
    return (
      moment.duration(Number(duration), "seconds").get("seconds") + " seconds"
    );
  };

  const renderTouchpoints = (tp: EntryInterface[]) => {
    return tp.map((item, idx1) => {
      return (
        <div key={idx1}>
          <DurationSummary>
            <span>Duration: {formatDuration(item.totalDuration)}</span>
            <span>
              Escalated duration: {formatDuration(item.escalatedDuration)}
            </span>
          </DurationSummary>
          <ActionTable>
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Interaction</TableCell>
                <TableCell>Coordinator</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {item.interactions.map((interaction, idx2) => {
                return interaction.actions.map((action, idx3) => {
                  return (
                    <TableRow key={idx3}>
                      <TableCell sx={{ width: "80px" }}>
                        {moment(action.timestamp).format("h:mma")}
                      </TableCell>
                      <TableCell sx={{ width: "140px" }}>
                        <ActionIcon title={action.type}>
                          {getActionIcon(action.type)}
                        </ActionIcon>{" "}
                        {action.type}
                      </TableCell>
                      <TableCell sx={{ width: "200px" }}>
                        {interaction.coordinatorName}
                      </TableCell>
                      <TableCell sx={{ width: "300px" }}>
                        {action.type === "note" && action.noteContent
                          ? `Note: ${action.noteContent}`
                          : ""}
                      </TableCell>
                    </TableRow>
                  );
                });
              })}
            </TableBody>
          </ActionTable>
        </div>
      );
    });
  };

  const getInteractionCount = (touchpoints: EntryInterface[]) => {
    return touchpoints.flatMap((touchpoint) =>
      touchpoint.interactions.flatMap((interaction) => interaction.actions)
    ).length;
  };

  const generateList = (rawList: EncounterInterface[]): BodyProps[] => {
    return rawList.map((item, idx) => {
      return {
        cells: [
          {
            content: (
              <Accordion key={idx}>
                <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                  <b>{moment(item.day).format("ddd, MMM DD")}</b>
                  <InteractionSummary>
                    {getInteractionCount(item.touchPoints)} interactions
                  </InteractionSummary>
                </AccordionSummary>
                <AccordionDetails>
                  {renderTouchpoints(item.touchPoints)}
                </AccordionDetails>
              </Accordion>
            )
          }
        ]
      };
    });
  };

  useEffect(() => {
    window.scroll({
      top: 172,
      left: 0,
      behavior: "smooth"
    });
    paginationAction(5, 0);
  }, []);

  return (
    <StyledContainer>
      <EncounterTimer />
      <Grid container>
        <Grid item md={1.5} lg={1} />
        <Grid item md={10.5} lg={11}>
          <StyledHeader>
            <h2>{t("u-encounters")}</h2>
          </StyledHeader>
          <SimpleGrid
            body={generateList(membersEncounters || [])}
            paginationAction={paginationAction}
            totalList={totalEncounterList}
          />
        </Grid>
      </Grid>
    </StyledContainer>
  );
}
